<template>
  <div v-if="loading" class="ui active centered inline loader">
    <div class="ui text loader">{{ $t('loading') }}...</div>
  </div>
  <div v-else style="padding-bottom: 200px" :key="visitInfoKey">
    <div
      class="ui labeled icon medium red button no-print"
      style="background-color: #092442"
      @click="goToReservation()"
    >
      <i class="left arrow icon"></i>
      Go to reservation
    </div>
    <div
      class="ui very compact medium orange right floated button no-print"
      style="background-color: #092442"
      @click="navigateTo('Home')"
    >
      <i class="home icon"></i>
      Home
    </div>
    <div
      class="ui very compact medium orange right floated button no-print"
      @click="goToUpdateVisit()"
      v-if="![VisitStatuses.FINISHED, VisitStatuses.CANCELLED].includes(visit.visit_status_id) "
    >
      Update
    </div>
    <div
      class="ui very compact medium red right floated button no-print"
      @click="openModal('cancel-visit')"
      v-if="![VisitStatuses.AFTER_WORK, VisitStatuses.FINISHED, VisitStatuses.CANCELLED].includes(visit.visit_status_id)"
    >
      Cancel
    </div>
    <div class="ui segment">
      <div class="centered-header">
        <h2 class="ui header" style="color: #092442;">
          Visit # {{ visit.id }} <br/><span style="text-decoration: underline"> {{ VisitStatuses[visit.visit_status_id]}}</span>
        </h2>
      </div>

      <visitInfo  
        :visit="visit" 
        :packages="packages" 
        :patient="patient"
        style="margin-top: 20px;"
      />

      <div v-if="[VisitStatuses.IN_VISIT, VisitStatuses.AFTER_WORK, VisitStatuses.FINISHED].includes(visit.visit_status_id)"> 
        <div class="ui divider no-print" style="margin-top: 20px; margin-bottom: 20px;"/>
        <div class="centered-header no-print">
          <h2 class="ui header" style="color: #092442;">
            Visit Details
          </h2>
        </div>
        <div class="ui hidden divider"></div>
        <div class="ui fluid tabular menu">
          <a class="item" :class="{ active: currentTab === tabs.first }"
            @click="currentTab = tabs.first">
            Prescription
          </a>
          <a class="item" :class="{ active: currentTab === tabs.second }"
            @click="currentTab = tabs.second">
            Medical history
          </a>
          <a class="item" :class="{ active: currentTab === tabs.third }"
            @click="currentTab = tabs.third">
            Follow up
          </a>
          <a class="item" :class="{ active: currentTab === tabs.fourth }"
            @click="currentTab = tabs.fourth">
            Medical tests
          </a>
          <a class="item" :class="{ active: currentTab === tabs.fifth }"
            @click="currentTab = tabs.fifth">
            Images
          </a>
          <a 
            class="item" 
            :class="{ active: currentTab === tabs.sixth }"
            @click="currentTab = tabs.sixth"
            v-if="medicalMachineQuantitivePackageIds().length > 0"
          >
            Medical machine usages
          </a>
        </div>
        <div 
          class="ui grid no-print" 
          style="margin-top: 10px"
        >
          <div class="sixteen wide stretched column">
            <keep-alive>
              <prescriptionPart
                :reservationVisitId="reservationVisitId"
                :prescription="visit.prescription"
                :canAdd="$auth.check('doctor') && [VisitStatuses.IN_VISIT, VisitStatuses.AFTER_WORK].includes(visit.visit_status_id)"
                v-if="currentTab === tabs.first"
              />  
            </keep-alive>
            <keep-alive>
              <diagnosePart
                :reservationVisitId="reservationVisitId"
                :diagnose="visit.diagnose"
                :canAdd="$auth.check('doctor') && [VisitStatuses.IN_VISIT, VisitStatuses.AFTER_WORK].includes(visit.visit_status_id)"
                v-if="currentTab === tabs.second"
              />  
            </keep-alive>

            <keep-alive>
              <followUpPart
                :reservationVisitId="reservationVisitId"
                :followup="visit.follow_up"
                :canAdd="$auth.check('doctor') && [VisitStatuses.IN_VISIT, VisitStatuses.AFTER_WORK].includes(visit.visit_status_id)"
                v-if="currentTab === tabs.third"
              />
            </keep-alive>

            <keep-alive>
              <medicalAnalysisPart
                :reservationVisitId="reservationVisitId"
                :medicalAnalysis="visit.medical_analysis"
                :canAdd="$auth.check('doctor') && [VisitStatuses.IN_VISIT, VisitStatuses.AFTER_WORK].includes(visit.visit_status_id)"
                v-if="currentTab === tabs.fourth"
              /> 
            </keep-alive>
            <keep-alive> 
              <imagesPart
                :reservationVisitId="reservationVisitId"
                :album="visit.album"
                :albumCallBack="albumCallBack"
                :canAdd="$auth.check('doctor') && [VisitStatuses.IN_VISIT, VisitStatuses.AFTER_WORK].includes(visit.visit_status_id)"
                v-if="currentTab === tabs.fifth"
              />
            </keep-alive>
            <keep-alive>
              <div v-if="currentTab === tabs.sixth">
                <div 
                  class="ui labeled icon medium red button no-print"
                  style="background-color: #092442; margin-bottom: 5px"
                  @click="packageServices += 1"
                  v-if="getPackagesForUsageForm().length > 1"
                >
                  Add another package services  
                </div>
                <div 
                  class="ui clearing green segment" 
                  v-for="packageServices in visit.medical_machine_visit_detials" 
                  :key="packageServices.id"
                >
                  <medicalMachinePackageUsage
                    :selectedPackage="getPackage(packageServices.package_id)"
                    :usages="packageServices.details"
                  />
                </div>
                <div v-if="getPackagesForUsageForm().length">
                  <div 
                    class="ui clearing orange segment" 
                    v-for="i in packageServices" 
                    :key="i"
                  >
                    <h2 class="ui header" style="text-align:center; text-decoration:underline">
                      Services Form
                    </h2> 
                    <medicalMachineUsagesForm 
                      :reservationVisitId="reservationVisitId"
                      :packages="getPackagesForUsageForm()"
                      @medical-machines-services-change="packageServicesEventListener"
                    />  
                  </div>
                </div>
              </div>
            </keep-alive>
          </div>
        </div>
      </div>
    </div>
    <div
      class="ui large longer modal"
      id="cancel-visit"
      style="margin-top: 20px !important;"
    >
      <div class="header">
        Visit Cancellation
      </div>

      <div class="content">
        <div class="ui form">
          <div class="field">
            <label for="" style="font-size: 15px">
              {{ $t('cancellationReason') }}
              <span style="font-size: 10px; color:green;">{{
                $t('notRequired')
              }}</span>
            </label>
            <textarea
              rows="3"
              :placeholder="$t('cancellationReason')"
              v-model="cancellation_reason"
            />
          </div>
        </div>
      </div>

      <div class="actions">
        <div class="ui button" @click="discard('cancel-visit')">
          {{ $t('discard') }}
        </div>
        <div
          class="ui blue button"
          style="background-color: #092442"
          @click="save('cancel-visit')"
        >
          {{ $t('save') }}
        </div>
      </div>
    </div>  
  </div>
</template>

<script>
export default {
  components: {
    medicalAnalysisPart: require('./visit_components/MedicalAnalysisPart').default,
    diagnosePart: require('./visit_components/DiagnosePart').default,
    followUpPart: require('./visit_components/FollowUpPart').default,
    prescriptionPart: require('./visit_components/PrescriptionPart').default,
    imagesPart: require('./visit_components/ImagesPart').default,
    visitInfo: require('./visit_components/VisitInfo').default,
    medicalMachineUsagesForm: require('./visit_components/MedicalMachineUsageForm').default,
    medicalMachinePackageUsage: require('./visit_components/MedicalMachinePackageUsages').default
  },
  data() {
    return {
      visit: {},
      patient: {},
      packages: [],
      loading: true,
      tabs: {
        first: 'prescription',
        second: 'diagnose',
        third: 'followUps',
        fourth: 'medicalTests',
        fifth: 'images',
        sixth: 'medicalMachineOperations'
      },
      currentTab: 'prescription',
      cancellation_reason: null,
      packageServices: 1,
      visitInfoKey: 0
    }
  },
  beforeMount() {
    this.$http.get(`reservation_visits/${this.$route.params.reservationVisitId}`, {
      params: {
        clinic_id: this.getClinicId()
      }
    }).then(response => {
      this.visit = response.data.visit
      this.packages = response.data.meta.packages
      this.patient = response.data.meta.patient
      this.loading = false
    })
  },
  computed: {
    reservationVisitId() {
      return Number(this.$route.params.reservationVisitId)
    }
  },
  methods: {
    goToReservation() {
      this.navigateTo(`Show_Reservation`, {
        key: 'params',
        value: { id: this.visit.reservation_id }
      })
    },
    albumCallBack(album) {
      this.visit.album = album
    },
    goToUpdateVisit() {
      this.navigateTo('Update_Visit', {
        key: 'params',
        value: { 
          visitId: this.visit.id,
          reservationId: this.visit.reservation_id,
          reservationVisitId: this.reservationVisitId 
        }
      })
    },
    getPackagesForUsageForm () {
      if (this.$auth.check('doctor') 
        && [this.VisitStatuses.IN_VISIT, this.VisitStatuses.AFTER_WORK].includes(this.visit.visit_status_id)) {
          const finishedPackageIds = this.visit.medical_machine_visit_detials.map( mmvd => mmvd.package_id)
          const visitPackageIds = this.visit.visit_details.map(vd => vd.package_id)
          return this.packages.filter(p => {
            return !finishedPackageIds.includes(p.id) && 
            visitPackageIds.includes(p.id) &&
            this.medicalMachineQuantitivePackageIds().includes(p.id)
          })
        }
      
      return []
    },
    openModal(modalId) {
      $(`#${modalId}`).modal('show');
    },
    discard(modalId) {
      $(`#${modalId}`).modal('hide');
      this.cancellation_reason = null
    },
    getPackage(packageId) {
      return this.packages.find(p => p.id === packageId)
    },
    save(modalId) {
      this.$http
        .post(`reservation_visits/${this.reservationVisitId }/cancel`, {
          cancellation_reason: this.cancellation_reason,
          clinic_id: this.getClinicId()
        })
        .then(response => {
          this.discard(modalId);
          this.navigateTo('Home')
        });
    },
    packageServicesEventListener (event) {
      this.visit.medical_machine_visit_detials.push(event.data.packageUsages)
      --this.packageServices
      ++this.visitInfoKey
    },
    medicalMachineQuantitivePackageIds () {
      return this.packages.filter(p => {
        return p.entity_type === this.MEDICAL_MACHINE_NUMBER && 
          this.UnitMeasurements.QUANTITIVE.includes(this.unitMeasurements.find(p.entity_unit_measurement)? this.unitMeasurements.find(p.entity_unit_measurement).name : '')
      }).map(p => p.id)
    }
  },
}
</script>

<style scoped>
.centered-header {
  display: flex;
  justify-content: center;
}
</style>



// WEBPACK FOOTER //
// src/components/visits/Visit.vue